import { useState, ReactNode, createContext, useContext } from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';

interface AccordionContextValue {
  openItem: string | null;
  setOpenItem: (id: string | null) => void;
  allowMultiple: boolean;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('Accordion components must be used within Accordion');
  }
  return context;
};

export interface AccordionProps {
  children: ReactNode;
  defaultOpenItem?: string;
  allowMultiple?: boolean;
  className?: string;
}

export function Accordion({
  children,
  defaultOpenItem,
  allowMultiple = false,
  className,
}: AccordionProps) {
  const [openItem, setOpenItem] = useState<string | null>(defaultOpenItem || null);

  return (
    <AccordionContext.Provider value={{ openItem, setOpenItem, allowMultiple }}>
      <div className={clsx('space-y-2', className)}>{children}</div>
    </AccordionContext.Provider>
  );
}

export interface AccordionItemProps {
  id: string;
  title: ReactNode;
  children: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  chevronClassName?: string;
}

export function AccordionItem({
  id,
  title,
  children,
  className,
  headerClassName,
  contentClassName,
  chevronClassName,
}: AccordionItemProps) {
  const { openItem, setOpenItem } = useAccordionContext();
  const isOpen = openItem === id;

  const handleToggle = () => {
    setOpenItem(isOpen ? null : id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div
      className={clsx(
        'overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800',
        className
      )}
    >
      {/* Header */}
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={clsx(
          'flex w-full items-center justify-between px-6 py-4 text-left transition-colors',
          'hover:bg-gray-50 dark:hover:bg-dark-700',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-dark-900',
          headerClassName
        )}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${id}`}
        id={`accordion-header-${id}`}
      >
        <div className="flex-1">{title}</div>
        <ChevronDown
          className={clsx(
            'h-5 w-5 shrink-0 text-gray-500 transition-transform duration-200 dark:text-gray-400',
            isOpen && 'rotate-180',
            chevronClassName
          )}
          aria-hidden="true"
        />
      </button>

      {/* Content */}
      <div
        id={`accordion-content-${id}`}
        role="region"
        aria-labelledby={`accordion-header-${id}`}
        className={clsx(
          'overflow-hidden transition-all duration-200 ease-in-out',
          isOpen ? 'max-h-screen' : 'max-h-0'
        )}
      >
        <div className={clsx('px-6 pb-4', contentClassName)}>{children}</div>
      </div>
    </div>
  );
}

Accordion.Item = AccordionItem;
